import { FieldValues, FormProvider, useForm } from "react-hook-form";
import { toast } from "react-toastify";

import { SCvalues } from "@/_helpers/commonComponentsTypes.ts";
import FormFieldWrapper from "@/common/components/form/FormFieldWrapper.tsx";
import Input from "@/common/components/form/Input";
import SelectForm from "@/common/components/form/SelectForm.tsx";
import { passwordRule, phoneRule } from "@/common/formValidation/index.ts";
import { IUser } from "@/modules/profile/interfaces/user";

import { useCreateUser, useUpdateUser } from "../../hooks/user.hooks.ts";

interface IUserFormProps {
  defaultValues?: IUser;
  teamId?: string;
  readOnly?: boolean;
  personTitleOptions: SCvalues[] | [];
  callbackOnSuccess?: () => void;
}

const UserForm: React.FC<IUserFormProps> = ({
  defaultValues,
  teamId,
  readOnly,
  callbackOnSuccess,
  personTitleOptions,
}) => {
  const { mutate: createUser } = useCreateUser({
    handleSuccess: (data) => {
      toast.success(
        `L'utilisateur ${(data as IUser)?.lastname} ${(data as IUser)?.firstname} a été ajouté !`
      );
      callbackOnSuccess && callbackOnSuccess();
    },
  });

  const { mutate: updateUser } = useUpdateUser({
    handleSuccess: (data) => {
      toast.success(
        `L'utilisateur ${(data as IUser)?.lastname} ${(data as IUser)?.firstname} a été mis à jour !`
      );
      callbackOnSuccess && callbackOnSuccess();
    },
  });

  // Find the title option that matches the defaultValues titleId
  const titleOption = personTitleOptions?.find(
    (item) => item?.value === defaultValues?.titleId
  );

  // Set up the form with modified default values
  const methods = useForm<FieldValues>({
    defaultValues: {
      ...defaultValues,
      titleId: titleOption,
    },
  });

  const onSubmit = async (data: any) => {
    try {
      delete data.permissions;
      if (!teamId) {
        throw "Id de l'équipe non trouvé.";
      }
      // set team id
      data.teams = [{ id: teamId }];
      // set title id
      data.titleId = data.titleId.value;

      if (data?.id) {
        // update
        updateUser(data);
      } else {
        // create
        delete data?.password;
        createUser(data);
      }
    } catch (e: any) {
      toast.error("Impossible de valider le formulaire");
    }
  };
  return (
    <>
      <FormProvider {...methods}>
        <form
          id="UserForm"
          onSubmit={methods.handleSubmit(onSubmit)}
          className="groupForm-form"
        >
          <div className="groupForm-form-details">
            <FormFieldWrapper>
              <SelectForm
                control={methods.control}
                label="Titre"
                name={"titleId"}
                options={personTitleOptions}
                rules={{ required: "Veuillez renseigner un titre" }}
                isMulti={false}
                disabled={readOnly}
              />
            </FormFieldWrapper>
            <Input
              control={methods.control}
              label="Nom"
              name={"lastname"}
              defaultValue=""
              rules={{ required: "Veuillez renseigner un nom" }}
              disabled={readOnly}
            />
            <Input
              control={methods.control}
              label="Prénom"
              name={"firstname"}
              defaultValue=""
              rules={{ required: "Veuillez renseigner un prénom" }}
              disabled={readOnly}
            />
            <Input
              control={methods.control}
              label="Téléphone"
              name={"phoneNumber"}
              defaultValue=""
              rules={{
                required: "Veuillez renseigner un numéro de téléphone",
                ...phoneRule,
              }}
              disabled={readOnly}
            />
            <Input
              control={methods.control}
              label="Téléphone fixe"
              name={"landLineNumber"}
              defaultValue=""
              disabled={readOnly}
              rules={{
                ...phoneRule,
              }}
            />
            <Input
              control={methods.control}
              label="Email"
              name={"email"}
              defaultValue=""
              rules={{ required: "Veuillez renseigner un email" }}
              disabled={readOnly}
            />
            <Input
              control={methods.control}
              label="Fonction"
              name={"occupation"}
              defaultValue=""
              disabled={readOnly}
            />
            <Input
              labelInfos="Doit contenir au moins une lettre minuscule, une majuscule, un chiffre et au moins 8 caractères"
              control={methods.control}
              hidden={!defaultValues}
              defaultValue=""
              name={"password"}
              type="password"
              disabled={readOnly}
              label="Mot de passe"
              rules={defaultValues ? { ...passwordRule } : {}}
            />
          </div>
        </form>
      </FormProvider>
    </>
  );
};

export default UserForm;
